import {
  DiscoverResult,
  ISdkManagedPaymentIntent,
  Reader,
  Terminal,
  loadStripeTerminal,
} from '@stripe/terminal-js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSession } from 'src/contexts/SessionContext';
import { fetchTerminalConnectionToken } from 'src/models/payment-methods/payment-methods';
import toast from 'src/utils/toast';

export function useStripeTerminal() {
  const [terminal, setTerminal] = useState<Terminal | null | undefined>(null);
  const [connectedReaders, setConnectedReaders] = useState<Reader[]>([]);
  const [selectedReader, setSelectedReader] = useState<Reader | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const { session } = useSession();
  const stripeLocationIds = useRef<string[]>([]);

  const fetchConnectionToken = async (): Promise<string> => {
    const secret = await fetchTerminalConnectionToken();
    return secret;
  };

  const fetchReadersForLocation = useCallback((terminal: Terminal) => {
    if (!terminal) return;

    const location = stripeLocationIds.current[0];
    if (!location) return;

    return terminal.discoverReaders({ location }).then((discoverResult) => {
      // eslint-disable-next-line no-console
      console.log(discoverResult, location);
      if ('error' in discoverResult && discoverResult.error) {
        // eslint-disable-next-line no-console
        console.log('Failed to discover: ', discoverResult.error);
      } else if ('errors' in discoverResult && discoverResult.errors) {
        // eslint-disable-next-line no-console
        console.log('Failed to discover: ', discoverResult.errors[0]);
      } else if (
        'discoveredReaders' in discoverResult &&
        discoverResult.discoveredReaders.length === 0
      ) {
        // eslint-disable-next-line no-console
        console.log('No available readers.');
      } else {
        const readers = (discoverResult as DiscoverResult).discoveredReaders;
        setConnectedReaders((prev) => {
          const map = new Map(prev.map((r) => [r.id, r]));
          readers.forEach((r) => map.set(r.id, r));
          return Array.from(map.values());
        });
      }

      if (stripeLocationIds.current.length) {
        stripeLocationIds.current = stripeLocationIds.current.filter(
          (id) => id !== location
        ); // remove
        if (stripeLocationIds.current.length) {
          fetchReadersForLocation(terminal);
        }
      }
    });
  }, []);

  const connectReaderHandler = useCallback(
    (session, terminal) => {
      if (!session || !terminal) return;
      terminal.cancelReadReusableCard();
      terminal.cancelCollectPaymentMethod();
      stripeLocationIds.current = session.stripeLocationIds || [];
      fetchReadersForLocation(terminal);
    },
    [fetchReadersForLocation]
  );

  const cancelReadReusableCard = async () => {
    if (!terminal) return;
    await terminal.cancelReadReusableCard();
  };

  useEffect(() => {
    const init = async () => {
      const stripeTerminal = await loadStripeTerminal();
      const t = stripeTerminal?.create({
        onFetchConnectionToken: fetchConnectionToken,
        onUnexpectedReaderDisconnect: () => {
          // eslint-disable-next-line no-console
          console.log('Disconnected from reader');
        },
      });
      setTerminal(t);
      fetchConnectionToken();
    };
    init();
  }, []);

  useEffect(() => {
    if (!terminal || !session) return;
    connectReaderHandler(session, terminal);
  }, [terminal, session, connectReaderHandler]);

  const cancelCollectPaymentMethod = async () => {
    if (!terminal) return;
    await terminal.cancelCollectPaymentMethod();
  };

  const connectReader = async (reader: Reader) => {
    if (!terminal) return;
    setConnecting(true);
    setConnectionError(null);

    try {
      await terminal.disconnectReader();
    } catch {
      // ignore
    }

    try {
      const connectResult = await terminal.connectReader(reader);
      if ('error' in connectResult && connectResult.error) {
        setConnectionError(connectResult.error.message);
        toast.error('Failed to connect: ' + connectResult.error.message);
      } else if ('reader' in connectResult) {
        setSelectedReader(reader);
        setConnectionError(null);
        toast.success('Connected to reader: ' + connectResult.reader.label);
      }
    } catch (err) {
      setConnectionError('unable to connect.');
    } finally {
      setConnecting(false);
    }
  };

  const retryReader = () => {
    if (selectedReader) {
      connectReader(selectedReader);
    }
  };

  const collectPaymentMethod = async (clientSecret: string) => {
    if (!terminal) throw new Error('Terminal not initialized');
    return terminal.collectPaymentMethod(clientSecret);
  };

  const processPayment = async (paymentIntent: ISdkManagedPaymentIntent) => {
    if (!terminal) throw new Error('Terminal not initialized');
    return terminal.processPayment(paymentIntent);
  };

  const errorMessage = (
    error,
    defaultMessage = 'Unable to capture payment method.'
  ) => {
    let message = defaultMessage;

    if (error.code) message = `${error.code} - `;
    if (error.message) message += error.message;

    return message;
  };

  const saveCardForLater = async (callback: (paymentMethod: any) => void) => {
    if (!terminal) return;
    const result = await terminal.readReusableCard();
    if ('error' in result && result.error) {
      // eslint-disable-next-line no-console
      console.error(`Sentry Error: ${result.error.message}`);
    } else if ('payment_method' in result) {
      callback(result.payment_method);
    }
  };

  return {
    terminal,
    connectedReaders,
    selectedReader,
    connecting,
    connectionError,
    connectReader,
    retryReader,
    collectPaymentMethod,
    processPayment,
    saveCardForLater,
    cancelCollectPaymentMethod,
    errorMessage,
    cancelReadReusableCard,
  };
}
